/**
 * Response types for Starlight's `GetAuditInsights` read.
 *
 * Hand-written rather than generated: Starlight has no TypeScript client, so the browser
 * calls it as REST through `apiClient`. These mirror
 * `GSCEStarlightModel/src/main/smithy/operations/get-audit-insights.smithy` — keep them in
 * step with it.
 *
 * Optionality here is the model's, not a convenience. Most prose fields are optional
 * because generation accepts a partial section rather than discarding a whole insight over
 * one absent field, so a renderer has to tolerate any of them being missing.
 */
import type { ResponseResult } from '@amzn/gbsa-helios-typescript-client';

/**
 * What a read can say about an audit's insight.
 *
 * Named rather than left as bare literals at the call sites, matching how the tab reads
 * every other closed set it owns. There is no generated client to take these from, so the
 * union is derived from the map — one place to edit when the model gains a status.
 */
export const INSIGHT_STATUS = {
  READY: 'READY',
  PENDING: 'PENDING',
  FAILED: 'FAILED',
  NOT_APPLICABLE: 'NOT_APPLICABLE',
} as const;

export type InsightStatus = (typeof INSIGHT_STATUS)[keyof typeof INSIGHT_STATUS];

/**
 * An auditor's result for a question.
 *
 * Taken from the Helios client rather than re-declared. Starlight's own model says
 * this enum "mirrors Helios's own vocabulary of the same name", and the UI already
 * reads question results through the Helios enum, so a second local copy of the same
 * three values is the drift `prefer-sdk-enums` warns about. If the two services ever
 * do diverge, the mismatch surfaces here as a type error, which is where it belongs.
 */
export type { ResponseResult };

/**
 * The audit's score, quoted verbatim from the audit — never recomputed here.
 *
 * Only `totalScored` is guaranteed. An audit that completed without being scored carries
 * the other four absent rather than zero, so absent means "not scored" and zero is a
 * scored result.
 */
export interface ScoreFacts {
  scorePercent?: number;
  passCount?: number;
  failCount?: number;
  naCount?: number;
  totalScored: number;
}

/** Something the audit did well. */
export interface Strength {
  /** The questions backing this. All passed. */
  questionIds: string[];
  behavior?: string;
  processPath?: string;
}

/** What held up on the audit: a one-line reading, then the specific strengths behind it. */
export interface Strengths {
  overview?: string;
  items?: Strength[];
}

/** A group of related failures, titled by what they have in common. */
export interface FailureTheme {
  themeName?: string;
  /** The questions backing this. All failed. */
  questionIds: string[];
  processPath?: string;
  insight?: string;
}

/** How an earlier audit answered the same question. */
export interface PriorResult {
  auditId: string;
  result: ResponseResult;
  auditCompletionDate?: string;
  respondedAt?: string;
  respondedBy?: string;
}

/** A question that failed here and had failed before. */
export interface RecurringFailureItem {
  questionId: string;
  questionTitle?: string;
  /** Up to three earlier answers, newest first. */
  priorResults?: PriorResult[];
  /** Capped at 4, so 4 means "at least 4". 1 means this failure did not follow another. */
  consecutiveFailCount: number;
}

/** An audit's failure history at its site. */
export interface RecurringFailures {
  /**
   * False means there was no history to compare against, which an empty `items` alone does
   * not distinguish from nothing having repeated. Drives the empty state.
   */
  hasPriorHistory: boolean;
  windowStart?: string;
  windowEnd?: string;
  items?: RecurringFailureItem[];
}

/** One generated insight. The score is the audit's own; the prose is model-generated. */
export interface InsightDocument {
  generatedAt: string;
  completionDate?: string;
  /** The locale this prose is in. Not necessarily the one requested. */
  language: string;
  score: ScoreFacts;
  executiveSummary: string;
  strengths?: Strengths;
  failureThemes?: FailureTheme[];
  recurringFailures?: RecurringFailures;
}

/**
 * A task raised by an earlier audit that is still open.
 *
 * The same `linkedQuestionId` can appear more than once — one entry per earlier audit that
 * raised a task still outstanding against that question.
 */
export interface OpenTaskRef {
  taskId: string;
  title?: string;
  /** As Helios reports it. Not a union: Helios may add statuses. */
  taskStatus: string;
  assigneeLogin?: string;
  dueDate?: string;
  createdAt?: string;
  createdBy?: string;
  linkedQuestionId: string;
  linkedQuestionTitle?: string;
  sourceAuditId: string;
}

export interface GetAuditInsightsRequest {
  businessUnitId: string;
  auditId: string;
  /** Server defaults to en-US and falls back to it, so callers may omit this. */
  language?: string;
}

export interface GetAuditInsightsResponse {
  status: InsightStatus;
  /** Present when `status` is READY. */
  insights?: InsightDocument;
  /** Present when `status` is FAILED. */
  reason?: string;
  /** Read live, so it reflects task state now rather than at generation. */
  openTasks?: OpenTaskRef[];
  /**
   * True when the open tasks could not be read in full. Absent on success. Distinguishes
   * "none are open" from "could not tell", so the tile can show a soft error instead of
   * claiming zero.
   */
  openTasksUnavailable?: boolean;
}
